/**
 * Attendances Store - Attendance State Management
 *
 * Handles:
 * - Loading attendances for a session
 * - Creating/updating attendances (with offline support)
 * - Sync queue for offline changes
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import axios from 'axios'
import { attendancesApi } from '@/api/attendances.api'
import { db } from '@/db'
import { isNetworkError } from '@/api/client'
import { useParticipantsStore } from '@/stores/participants.store'
import { useRegistrationsStore } from '@/stores/registrations.store'
import { useSessionsStore } from '@/stores/sessions.store'
import type { AttendanceDto } from '@/types/api.types'
import type { LocalAttendance, SyncStatus } from '@/types/local.types'

export const useAttendancesStore = defineStore('attendances', () => {
  // ==========================================================================
  // State
  // ==========================================================================

  // Server attendances by session ID
  const attendances = ref<Map<number, AttendanceDto[]>>(new Map())

  // Local (offline) attendances by session ID
  const localAttendances = ref<Map<number, LocalAttendance[]>>(new Map())

  const isLoading = ref(false)
  const isSaving = ref(false)
  const error = ref<string | null>(null)

  // ==========================================================================
  // Getters
  // ==========================================================================

  /**
   * Get attendances for a session (merged: server + local pending)
   */
  const getBySessionId = computed(() => {
    return (sessionId: number): AttendanceDto[] => {
      return attendances.value.get(sessionId) || []
    }
  })

  /**
   * Get local attendances for a session
   */
  const getLocalBySessionId = computed(() => {
    return (sessionId: number): LocalAttendance[] => {
      return localAttendances.value.get(sessionId) || []
    }
  })

  /**
   * Check if a participant is present in a session
   */
  const isPresent = computed(() => {
    return (sessionId: number, personId: number): boolean => {
      // Lokaler (noch nicht synchronisierter) Stand hat Vorrang — dort ist `isPresent` die
      // Absicht des Nutzers, nicht der Serverzustand.
      const local = getLocalBySessionId.value(sessionId)
      const localRecord = local.find((a) => a.personId === personId)
      if (localRecord) return localRecord.isPresent

      // Serverseitig gilt „Existenz = Anwesenheit": ein Datensatz bedeutet anwesend. Es gibt
      // kein `isPresent`-Feld in der Antwort — es aus der Antwort zu lesen hieße, jeden
      // Teilnehmer als abwesend anzuzeigen.
      return getBySessionId.value(sessionId).some((a) => a.personId === personId)
    }
  })

  /**
   * Get attendance record for a participant in a session
   */
  const getAttendance = computed(() => {
    return (sessionId: number, personId: number): AttendanceDto | LocalAttendance | undefined => {
      // Check local first
      const local = getLocalBySessionId.value(sessionId)
      const localRecord = local.find((a) => a.personId === personId)
      if (localRecord) return localRecord

      // Check server data
      const server = getBySessionId.value(sessionId)
      return server.find((a) => a.personId === personId)
    }
  })

  /**
   * Anwesenheiten ausgetretener Mitglieder eines Termins, nach Namen sortiert. Sie sind
   * keine Gäste; die Ansicht zeigt sie als normale Teilnehmer, wo keine Registrierung
   * für den Termin vorliegt (etwa gelöschte Doppelungen).
   */
  const getFormerMembersBySessionId = computed(() => {
    return (sessionId: number): AttendanceDto[] =>
      getBySessionId
        .value(sessionId)
        .filter((a) => a.isFormerMember)
        .sort((a, b) => (a.participantName || '').localeCompare(b.participantName || '', 'de'))
  })

  /**
   * Get attendance count for a session
   */
  const getPresentCount = computed(() => {
    return (sessionId: number): number => {
      const server = getBySessionId.value(sessionId)
      const local = getLocalBySessionId.value(sessionId)

      // Create a set of person IDs that are marked present
      const presentIds = new Set<number>()

      // Jeder Server-Datensatz bedeutet anwesend („Existenz = Anwesenheit")
      server.forEach((a) => presentIds.add(a.personId))

      // Override with local records
      local.forEach((a) => {
        if (a.isPresent) {
          presentIds.add(a.personId)
        } else {
          presentIds.delete(a.personId)
        }
      })

      return presentIds.size
    }
  })

  /**
   * Check if there are pending sync items for a session
   */
  const hasPendingSync = computed(() => {
    return (sessionId: number): boolean => {
      const local = getLocalBySessionId.value(sessionId)
      return local.some((a) => a.syncStatus === 'pending')
    }
  })

  // ==========================================================================
  // Actions
  // ==========================================================================

  /**
   * Fetch attendances for a session
   */
  async function fetchBySession(sessionId: number, forceRefresh = false): Promise<void> {
    // Return cached if available and not forcing refresh
    if (!forceRefresh && attendances.value.has(sessionId)) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      // Try API first
      const data = await attendancesApi.getBySession(sessionId)
      attendances.value.set(sessionId, data)

      // Also load any local pending changes
      await loadLocalAttendances(sessionId)
    } catch (e) {
      if (isNetworkError(e)) {
        // Try to load from cache
        await loadFromCache(sessionId)
        await loadLocalAttendances(sessionId)

        if (!attendances.value.has(sessionId)) {
          error.value = 'Offline - Keine gecachten Daten verfügbar'
        }
      } else {
        error.value = e instanceof Error ? e.message : 'Fehler beim Laden der Anwesenheiten'
        throw e
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Toggle attendance for a participant
   * Works offline by storing in local db and sync queue
   */
  async function toggleAttendance(
    sessionId: number,
    personId: number,
    isPresent: boolean
  ): Promise<void> {
    error.value = null

    const existingServer = getBySessionId.value(sessionId).find((a) => a.personId === personId)
    const existingLocal = getLocalBySessionId.value(sessionId).find((a) => a.personId === personId)

    if (!navigator.onLine) {
      // Offline: check if toggle reverts to the original server state.
      // Serverzustand = Existenz des Datensatzes.
      const originalIsPresent = existingServer !== undefined
      if (isPresent === originalIsPresent && existingLocal) {
        // Net change is zero (e.g. ON→OFF when server was OFF) — cancel pending change.
        // removeLocalAttendance() räumt den Queue-Eintrag mit ab.
        await removeLocalAttendance(sessionId, existingLocal.localId)
        return
      }
      // "Abwesend" ohne Server-Datensatz ist schon der Ist-Zustand (Existenz =
      // Anwesenheit) — es gibt nichts zu speichern und nichts zu synchronisieren.
      if (!isPresent && !existingServer) {
        return
      }

      // Store locally (instant, no need for isSaving lock)
      await saveLocalAttendance(sessionId, personId, isPresent, existingServer?.id)
      return
    }

    isSaving.value = true
    try {
      // Online: Update server directly
      //
      // Die API kennt kein isPresent-Feld — ihr Modell ist "Existenz = Anwesenheit"
      // (siehe docs/BUGFIX_ATTENDANCE_ABSENT.md). Deshalb:
      //   anwesend  → Datensatz anlegen (bzw. vorhandenen belassen)
      //   abwesend  → Datensatz loeschen (bzw. gar nichts tun, wenn keiner da ist)
      if (existingServer && !isPresent) {
        // Abwesend: Datensatz loeschen und aus dem Server-State entfernen
        await attendancesApi.delete(existingServer.id)

        const serverAttendances = attendances.value.get(sessionId) || []
        attendances.value.set(
          sessionId,
          serverAttendances.filter((a) => a.id !== existingServer.id)
        )
      } else if (existingServer) {
        // Anwesend und Datensatz existiert schon: serverseitig ist nichts zu tun. Es gibt kein
        // PUT mehr (`PUT /api/Attendances/{id}` wurde am 2026-08-04 entfernt und antwortet 404),
        // und es gäbe auch nichts zu ändern — die Existenz des Datensatzes IST die Anwesenheit.
      } else if (isPresent) {
        // Create new attendance
        const created = await attendancesApi.create({
          courseSessionId: sessionId,
          personId,
        })

        // Add to local state
        const serverAttendances = attendances.value.get(sessionId) || []
        attendances.value.set(sessionId, [...serverAttendances, created])
      }
      // else: abwesend ohne Server-Datensatz = schon der Ist-Zustand, kein Request

      // Remove any local pending for this person
      if (existingLocal) {
        await removeLocalAttendance(sessionId, existingLocal.localId)
      }
    } catch (e) {
      if (isNetworkError(e)) {
        // Network error: Save locally
        await saveLocalAttendance(sessionId, personId, isPresent, existingServer?.id)
      } else {
        error.value = e instanceof Error ? e.message : 'Fehler beim Speichern'
        throw e
      }
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Save attendance locally (for offline)
   */
  async function saveLocalAttendance(
    sessionId: number,
    personId: number,
    isPresent: boolean,
    serverId?: number
  ): Promise<void> {
    const existingLocal = getLocalBySessionId.value(sessionId).find((a) => a.personId === personId)

    const localAttendance: LocalAttendance = {
      localId: existingLocal?.localId || uuidv4(),
      courseSessionId: sessionId,
      personId,
      isPresent,
      syncStatus: 'pending' as SyncStatus,
      createdAt: existingLocal?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    // Only set serverId if it's defined
    if (serverId !== undefined) {
      localAttendance.serverId = serverId
    }

    // Save to IndexedDB
    await db.attendances.put(localAttendance)

    // Add to sync queue.
    // "abwesend" mit vorhandenem Server-Datensatz ist ein DELETE; alles andere ein 'create'
    // (POST ist serverseitig ein Upsert). Ein 'update' gibt es nicht mehr — der Endpoint
    // PUT /api/Attendances/{id} existiert nicht.
    await db.syncQueue.put({
      type: 'attendance',
      action: serverId && !isPresent ? 'delete' : 'create',
      localId: localAttendance.localId,
      data: localAttendance,
      createdAt: new Date().toISOString(),
      retryCount: 0,
    })

    // Update local state
    const local = localAttendances.value.get(sessionId) || []
    const existingIndex = local.findIndex((a) => a.personId === personId)
    if (existingIndex >= 0) {
      local[existingIndex] = localAttendance
    } else {
      local.push(localAttendance)
    }
    localAttendances.value.set(sessionId, [...local])
  }

  /**
   * Remove local attendance — inklusive des zugehörigen Sync-Queue-Eintrags.
   *
   * Beides gehört zusammen: der lokale Datensatz IST die vorgemerkte Änderung, der Queue-Eintrag
   * ihr Auftrag. Wird nur der Datensatz gelöscht, bleibt ein Auftrag ohne Absicht liegen — stellt
   * der Nutzer offline auf „abwesend" (Queue-Eintrag `delete`) und online wieder auf „anwesend",
   * würde der nächste Sync den Server-Datensatz trotzdem löschen und die Anwesenheit still
   * verschwinden.
   */
  async function removeLocalAttendance(sessionId: number, localId: string): Promise<void> {
    await db.attendances.where('localId').equals(localId).delete()
    await db.syncQueue.where('localId').equals(localId).delete()

    const local = localAttendances.value.get(sessionId) || []
    localAttendances.value.set(
      sessionId,
      local.filter((a) => a.localId !== localId)
    )
  }

  /**
   * Load local attendances from IndexedDB
   */
  async function loadLocalAttendances(sessionId: number): Promise<void> {
    try {
      const local = await db.attendances.where('courseSessionId').equals(sessionId).toArray()
      localAttendances.value.set(sessionId, local)
    } catch (e) {
      console.warn('[AttendancesStore] Failed to load local attendances:', e)
    }
  }

  /**
   * Load attendances from cache
   */
  async function loadFromCache(sessionId: number): Promise<void> {
    // For now, we only have local changes cached, not server data
    // This could be extended to cache server responses
    await loadLocalAttendances(sessionId)
  }

  /**
   * Sync pending attendances to server
   */
  async function syncPending(): Promise<{ success: number; failed: number; rejected: string[] }> {
    const pending = await db.syncQueue.where('type').equals('attendance').toArray()

    let success = 0
    let failed = 0
    /** Endgültig abgelehnte Einträge, als Satz für die Meldung an den Trainer */
    const rejected: string[] = []

    for (const item of pending) {
      try {
        const localData = item.data as LocalAttendance

        // "abwesend" wird immer als DELETE ausgefuehrt — unabhaengig davon, welche
        // action im Queue-Eintrag steht. Aeltere App-Versionen haben solche
        // Aenderungen als 'update' eingereiht; ein PUT {isPresent:false} ist
        // serverseitig ein No-Op und wuerde die Aenderung still verschlucken.
        if (!localData.isPresent) {
          if (localData.serverId !== undefined) {
            await attendancesApi.delete(localData.serverId)

            const serverAttendances = attendances.value.get(localData.courseSessionId) || []
            attendances.value.set(
              localData.courseSessionId,
              serverAttendances.filter((a) => a.id !== localData.serverId)
            )
          }
          // Ohne serverId existiert serverseitig kein Datensatz = schon abwesend.
          // Nichts zu senden, der Queue-Eintrag wird unten regulaer aufgeraeumt.
        } else if (localData.serverId === undefined) {
          // "anwesend" ohne Server-Datensatz → anlegen. Der POST ist serverseitig ein Upsert,
          // ein doppelter Sync erzeugt also kein Duplikat.
          const created = await attendancesApi.create({
            courseSessionId: localData.courseSessionId,
            personId: localData.personId,
          })

          // Update server state
          const serverAttendances = attendances.value.get(localData.courseSessionId) || []
          attendances.value.set(localData.courseSessionId, [...serverAttendances, created])
        }
        // "anwesend" MIT serverId: der Datensatz existiert schon, damit ist die Person anwesend —
        // nichts zu senden. (Solche Einträge entstehen nicht mehr; ältere App-Versionen haben sie
        // als action 'update' eingereiht, und ein PUT gibt es nicht mehr.) Der Queue-Eintrag wird
        // unten regulär aufgeräumt.

        await entferneAusWarteschlange(item.id!, localData)
        success++
      } catch (e) {
        // Endgültige Ablehnung (Mitglied zum Termin bereits ausgetreten): ein Wiederholen
        // ändert nichts, der Eintrag hinge sonst für immer in der Warteschlange. Er wird
        // verworfen und dem Trainer einmal mit Grund gemeldet. Nur der ausdrücklich
        // gekennzeichnete 409 zählt — ein anderer Konflikt kann sich auflösen und wird
        // wie jeder Fehler wiederholt.
        if (
          axios.isAxiosError(e) &&
          e.response?.status === 409 &&
          (e.response.data as { code?: string } | undefined)?.code === 'member-exited'
        ) {
          const localData = item.data as LocalAttendance
          await entferneAusWarteschlange(item.id!, localData)
          rejected.push(beschreibeAblehnung(localData, e.response.data))
          continue
        }

        // Update retry count
        await db.syncQueue.update(item.id!, {
          retryCount: item.retryCount + 1,
          lastError: e instanceof Error ? e.message : 'Unknown error',
        })
        failed++
      }
    }

    return { success, failed, rejected }
  }

  /** Queue-Eintrag samt lokaler Vormerkung entfernen — nach Erfolg wie nach endgültiger Ablehnung */
  async function entferneAusWarteschlange(itemId: number, localData: LocalAttendance) {
    await db.syncQueue.delete(itemId)
    await db.attendances.where('localId').equals(localData.localId).delete()

    const local = localAttendances.value.get(localData.courseSessionId) || []
    localAttendances.value.set(
      localData.courseSessionId,
      local.filter((a) => a.localId !== localData.localId)
    )
  }

  /**
   * Satz für die Meldung an den Trainer: wer, an welchem Termin, warum. Für Ausgetretene
   * liefert das Backend keine Registrierung mehr — deshalb zusätzlich der
   * Teilnehmer-Zwischenspeicher als Namensquelle.
   */
  function beschreibeAblehnung(localData: LocalAttendance, antwort: unknown): string {
    const grund = (antwort as { title?: string } | undefined)?.title || 'vom Server abgelehnt'

    const registrierung = useRegistrationsStore().getByParticipantId(localData.personId)
    const teilnehmer = useParticipantsStore().getById(localData.personId)
    const name =
      [registrierung?.participantFirstName, registrierung?.participantLastName]
        .filter(Boolean)
        .join(' ') ||
      [teilnehmer?.firstName, teilnehmer?.lastName].filter(Boolean).join(' ') ||
      'Ein Mitglied'

    const termin = useSessionsStore().getById(localData.courseSessionId)
    const datum = termin?.date
      ? `, Termin ${new Date(termin.date).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })}`
      : ''

    return `${name}${datum}: ${grund}`
  }

  /**
   * Clear session data
   */
  function clearSession(sessionId: number): void {
    attendances.value.delete(sessionId)
    localAttendances.value.delete(sessionId)
  }

  /**
   * Reset store
   */
  function $reset(): void {
    attendances.value.clear()
    localAttendances.value.clear()
    isLoading.value = false
    isSaving.value = false
    error.value = null
  }

  return {
    // State
    attendances,
    localAttendances,
    isLoading,
    isSaving,
    error,

    // Getters
    getBySessionId,
    getLocalBySessionId,
    isPresent,
    getAttendance,
    getPresentCount,
    getFormerMembersBySessionId,
    hasPendingSync,

    // Actions
    fetchBySession,
    toggleAttendance,
    syncPending,
    clearSession,
    $reset,
  }
})
